import { gsap } from "gsap";
import { GlowFilter } from "pixi-filters";
import { Container, IDestroyOptions } from "pixi.js";
import { nameof } from "../../../../shared/bindable-data";
import { VariableSaver } from "../../../../tools/variableSaver/VariableSaver";
import { TextWithTime, TextWithTimeState } from "../../common/components/TextWithTime";
import { IApplicationModel } from "../../common/models/IApplicationModel";
import { IGuestModel } from "../../common/models/IGuestModel";
import { Event } from "../../../../shared/event-system/Event";
import { ButtonWithScaleAnimation } from "../../common/components/ButtonWithScaleAnimation";
import { LobbyButtonsTextStyle } from "./LobbyButtonsTextStyle";
import { IFortuneWheelModel } from "../../popups/hourlyBonus/model/IFortuneWheelModel";
import { HourlyBonusState } from "../../popups/hourlyBonus/mainHourlyBonus/HourlyBonusState";
import { GameResourcePackage } from "../resources/GameResourcePackage";

export class HourlyBonusButton extends Container {
    private static readonly DICE_ROLLS_TEXT = "NEW GIFT IN\n";
    private static readonly BONUS_WHEEL_TEXT = "FREE SPIN IN\n";
    private static readonly SPIN_NOW = "COLLECT";
    private static readonly WHITE_FILTER_DEFAULT_ALPHA = 1;
    private static readonly PINK_FILTER_DEFAULT_ALPHA = 0.5;
    private static readonly SWITCH_ICON_REWARD_INDEX = 3;

    public readonly onStyleChanged: Event<void>;
    public readonly onClaimHourlyBonusRewardClick: Event<void>;
    public readonly onHourlyBonusSceneRequested: Event<HourlyBonusState>;

    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly applicationModel: IApplicationModel;
    private readonly gameResources: GameResourcePackage;
    private readonly variableSaver: VariableSaver;
    private readonly guestModel: IGuestModel;
    private bonusIcon: ButtonWithScaleAnimation;
    private freeSpinTimeText: TextWithTime;
    private filterWhite: GlowFilter;
    private filterPink: GlowFilter;
    private currentTextState: TextWithTimeState;

    public constructor(
        fortuneWheelModel: IFortuneWheelModel,
        applicationModel: IApplicationModel,
        gameResources: GameResourcePackage,
        variableSaver: VariableSaver,
        guestModel: IGuestModel
    ) {
        super();
        this.fortuneWheelModel = fortuneWheelModel;
        this.applicationModel = applicationModel;
        this.gameResources = gameResources;
        this.variableSaver = variableSaver;
        this.guestModel = guestModel;
        this.onStyleChanged = new Event();
        this.onClaimHourlyBonusRewardClick = new Event();
        this.onHourlyBonusSceneRequested = new Event();
        this.filterWhite = new GlowFilter({ distance: 15, outerStrength: 3, innerStrength: 0, color: 0xFFFFFF, quality: 0.1, alpha: 0.4 });
        this.filterPink = new GlowFilter({ distance: 40, outerStrength: 2, innerStrength: 0, color: 0xEB00FF, quality: 0.1, alpha: 0.5 });
        this.build();
        this.bonusIcon.onUp.add(this.onIconClickHandle, this);
        this.fortuneWheelModel.propertyChanged.add(this.onFortuneWheelModelPropertyChanged, this);
        this.freeSpinTimeText.onComplete.add(this.onTimeTextComplete, this);
        this.freeSpinTimeText.eventMode = 'static';
        this.freeSpinTimeText.on('pointerup', this.onTextClickHandle, this);
        this.once("added", this.show);
    }

    private async onFortuneWheelModelPropertyChanged(model: IFortuneWheelModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "spinAvailableIn"):
                this.freeSpinTimeText.updateTime(model.spinAvailableIn.value || 0);
                if (model.spinAvailableIn.value > 0) {
                    this.freeSpinTimeText.style.fontSize = 32;
                    this.filterWhite.alpha = 0;
                    this.filterPink.alpha = 0;
                }
                if (this.freeSpinTimeText.currentState !== this.currentTextState) {
                    this.onStyleChanged.emit();
                    this.currentTextState = this.freeSpinTimeText.currentState;
                }
                break;
            case nameof(model, "currentHourlyBonusSection"):
                const currentHourlyBonusSection = model.currentHourlyBonusSection.value;
                this.onStyleChanged.emit();
                if (currentHourlyBonusSection == 0 || currentHourlyBonusSection == HourlyBonusButton.SWITCH_ICON_REWARD_INDEX) {
                    this.changeButtonView(currentHourlyBonusSection == HourlyBonusButton.SWITCH_ICON_REWARD_INDEX);
                }
                this.freeSpinTimeText.position.x = (this.bonusIcon.width - this.freeSpinTimeText.width) * 0.5 + 10;
                break;
        }
    }

    private changeButtonView(isWheelReward: boolean): void {
        if (isWheelReward) {
            this.bonusIcon.defaultView.texture = this.gameResources.assets_views_game_ui_wheel_icon_gift_wheel_png;
            this.freeSpinTimeText.updateDefaultText(HourlyBonusButton.BONUS_WHEEL_TEXT);
        } else {
            this.bonusIcon.defaultView.texture = this.gameResources.assets_views_game_ui_wheel_icon_gift_dices_png;
            const defaultTimeText = HourlyBonusButton.DICE_ROLLS_TEXT;
            this.freeSpinTimeText.updateDefaultText(defaultTimeText);
        }
    }

    private build(): void {
        this.bonusIcon = new ButtonWithScaleAnimation(this.gameResources.assets_views_game_ui_wheel_icon_gift_dices_png);
        this.bonusIcon.pivot.set(this.bonusIcon.width * 0.5, this.bonusIcon.height * 0.5);
        this.bonusIcon.position.set(this.bonusIcon.width * 0.5, this.bonusIcon.height * 0.5);
        this.addChild(this.bonusIcon);
        this.bonusIcon.filters = [this.filterWhite, this.filterPink];

        const defaultTimeText = HourlyBonusButton.DICE_ROLLS_TEXT;

        this.freeSpinTimeText = new TextWithTime(this.fortuneWheelModel.spinAvailableIn.value || 0, defaultTimeText, HourlyBonusButton.SPIN_NOW, new LobbyButtonsTextStyle());
        this.freeSpinTimeText.position.set((this.bonusIcon.width - this.freeSpinTimeText.width) * 0.5 + 10, this.bonusIcon.height + 5);
        this.addChild(this.freeSpinTimeText);
        this.freeSpinTimeText.filters = [this.filterWhite, this.filterPink];
        this.currentTextState = this.freeSpinTimeText.currentState;
    }

    private show(): void {
        this.onFortuneWheelModelPropertyChanged(this.fortuneWheelModel, "spinAvailableIn");
        this.onFortuneWheelModelPropertyChanged(this.fortuneWheelModel, "currentHourlyBonusSection");
    }

    private async onTimeTextComplete(): Promise<void> {
        this.freeSpinTimeText.style.fontSize = 42;
        this.freeSpinTimeText.position.x = (this.bonusIcon.width - this.freeSpinTimeText.width) * 0.5 + 10;
        this.onStyleChanged.emit();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.filterWhite, { alpha: HourlyBonusButton.WHITE_FILTER_DEFAULT_ALPHA, duration: 0.5 });
        animationTimeLine.to(this.filterPink, { alpha: HourlyBonusButton.PINK_FILTER_DEFAULT_ALPHA, duration: 0.5 }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private onIconClickHandle(): void {
        this.onHourlyBonusSceneRequested.emit(HourlyBonusState.DEFAULT);
    }

    private onTextClickHandle(): void {
        if (this.fortuneWheelModel.currentHourlyBonusSection.value == HourlyBonusButton.SWITCH_ICON_REWARD_INDEX) {
            this.processOnWheelClick()
        }
        else {
            this.processOnDicesClick();
        }
    }

    private processOnWheelClick(): void {
        const isWheel = !this.isFirstWheelClaim() && this.fortuneWheelModel.spinAvailableIn.value == 0;
        this.onHourlyBonusSceneRequested.emit(isWheel ? HourlyBonusState.DAILY : HourlyBonusState.DEFAULT);
    }

    private isFirstWheelClaim(): boolean {
        return !this.variableSaver.exist(`${"WasFirstWheelClaim"},${this.guestModel.id.value}`);
    }

    private async processOnDicesClick(): Promise<void> {
        if (this.isFirstDicesClaim()) {
            this.onHourlyBonusSceneRequested.emit(HourlyBonusState.DEFAULT);
        }
        else {
            this.proccesNotFirstDicesClaim();
        }
    }

    private isFirstDicesClaim(): boolean {
        return !this.variableSaver.exist(`${"WasFirstDicesClaim"},${this.guestModel.id.value}`);
    }

    private proccesNotFirstDicesClaim(): void {
        if (this.fortuneWheelModel.spinAvailableIn.value > 0) {
            this.onHourlyBonusSceneRequested.emit(HourlyBonusState.DEFAULT);
        } else {
            this.onClaimHourlyBonusRewardClick.emit();
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.fortuneWheelModel.propertyChanged.remove(this.onFortuneWheelModelPropertyChanged, this);
        this.freeSpinTimeText.onComplete.remove(this.onTimeTextComplete, this);
        this.freeSpinTimeText.off('pointerup', this.onTextClickHandle, this);
        this.bonusIcon.onUp.remove(this.onIconClickHandle, this);
        super.destroy(options);
    }
}

